export type PlatformAuditFilters = {
  tenantId?: string;
  actorUserId?: string;
  action?: string;
  entityType?: string;
  entityId?: string;
  from?: string;
  to?: string;
  pageSize?: number;
};

export type PlatformAuditEvent = {
  id: string;
  signupIntentId: string | null;
  tenantId: string | null;
  actorUserId: string | null;
  actorDisplayName: string | null;
  actorEmail: string | null;
  action: string;
  entityType: string;
  entityId: string;
  result: string | null;
  reason: string | null;
  dataRedactedJson: string;
  correlationId: string | null;
  occurredAt: string;
};

export type PlatformAuditPage = {
  items: PlatformAuditEvent[];
  nextCursor: string | null;
  hasMore: boolean;
};

function apiError(response: Response, payload: unknown): Error {
  const problem = payload as { detail?: string; title?: string; correlationId?: string };
  const error = new Error(problem.detail ?? problem.title ?? "Platform audit request failed.");
  Object.assign(error, {
    status: response.status,
    problem: {
      ...(payload && typeof payload === "object" ? payload : {}),
      correlationId: problem.correlationId ?? response.headers.get("X-Correlation-Id") ?? undefined
    }
  });
  return error;
}

function setText(query: URLSearchParams, key: string, value: string | undefined): void {
  const normalized = value?.trim();
  if (normalized) query.set(key, normalized);
}

function toUtc(value: string | undefined): string | null {
  if (!value?.trim()) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

export async function getPlatformAuditEvents(
  token: string,
  filters: PlatformAuditFilters,
  cursor: string | null = null,
  signal?: AbortSignal
): Promise<PlatformAuditPage> {
  const query = new URLSearchParams();
  setText(query, "tenantId", filters.tenantId);
  setText(query, "actorUserId", filters.actorUserId);
  setText(query, "action", filters.action);
  setText(query, "entityType", filters.entityType);
  setText(query, "entityId", filters.entityId);
  const from = toUtc(filters.from);
  const to = toUtc(filters.to);
  if (from) query.set("from", from);
  if (to) query.set("to", to);
  if (cursor) query.set("cursor", cursor);
  query.set("pageSize", String(Math.min(100, Math.max(10, filters.pageSize ?? 30))));

  const response = await fetch(`/api/v1/platform-console/audit/events?${query.toString()}`, {
    method: "GET",
    signal,
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`
    }
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw apiError(response, payload);
  return payload as PlatformAuditPage;
}
